// dependencies
import React from "react";

// components
import EmployeeCard from "../../components.smart/EmployeeCard/EmployeeCard";
import { Grid, GridCol } from "../../components/Grid";
import PageTitle from "../../components/PageTitle";
import Panel from "../../components/Panel";
import SideNavigation, { SideNavigationLink } from "../../components/SideNavigation";
import Spacer from "../../components/Spacer";

// hooks
import { useIsNewNavigation } from "../../api/hooks/defs/navigation";
import usePageWidthBreakpoint from "../../hooks/usePageWidthBreakpoint";

// css
import css from "./InfoPageTemplate.module.scss";

export type InfoPageTemplateProps = {
	/**
	 * Action buttons to be displayed on the upper right side of the page.
	 * Expects a list of <Button> (button) components.
	 */
	actionButtons?: Array<React.ReactNode>;

	/**
	 * Action buttons to be displayed on the upper right side of the page, just below the primary.
	 * Expects a list of <Button> (button) components.
	 */
	secondaryActionButtons?: Array<React.ReactNode>;

	/**
	 * The key of the active navigation link.
	 * Expects a string.
	 */
	activeNavigationLinkKey?: string;

	/**
	 * The drawer to be displayed on the page.
	 * Expects a <Panel>  or <FilterPanel> component.
	 * If provided, the drawer will be rendered.
	 * If not provided, no drawer will be rendered.
	 */
	drawer?: React.ReactElement<typeof Panel>;

	/**
	 * The employee ID of the user.
	 * Expects a string.
	 * If provided, an <EmployeeCard> (employee card) component will be rendered.
	 * If not provided, no <EmployeeCard> (employee card) component will be rendered.
	 */
	employeeID?: string;

	/**
	 * The main content of the page.
	 * Expects a React node.
	 */
	mainContent?: React.ReactNode;

	/**
	 * The navigation links to be displayed on the left side of the page.
	 * Expects a list of SideNavigationLink objects.
	 */
	navigationLinks?: SideNavigationLink[];

	/**
	 * side navigation menu override
	 */
	sideNavigationMenuOverride?: React.ReactNode;
	/**
	 * The title of the page.
	 * Expects a string.
	 * If provided, a <PageTitle> component will be rendered.
	 * If not provided, no <PageTitle> component will be rendered.
	 */
	pageTitle?: string;

	/**
	 * The toolbarInformationRow to be displayed on the page just under the searchbar but above the main content.
	 * Expects a React node.
	 * If provided, the toolbarInformationRow will be rendered.
	 * If not provided, no toolbarInformationRow will be rendered.
	 */
	toolbarInformationRow?: React.ReactNode;

	/**
	 * toolbar Action Row sits on the top left of the main column
	 * Expects a React node.
	 * A standard one can be found in webserver/assets/react/pageTemplates/InfoPageTemplate/StandardToolbarActionRow/index.tsx
	 */
	toolbarActionRow?: React.ReactNode;

	/**
	 * Visible page heading rendered above the toolbar inside the main content column.
	 * Used by side-nav-era pages where the legacy sub-nav (which provided context) is removed.
	 */
	pageHeading?: React.ReactNode;
};

const InfoPageTemplate = ({
	actionButtons,
	activeNavigationLinkKey,
	drawer,
	employeeID,
	mainContent,
	navigationLinks,
	pageHeading,
	pageTitle,
	secondaryActionButtons,
	sideNavigationMenuOverride,
	toolbarInformationRow,
	toolbarActionRow,
}: InfoPageTemplateProps) => {
	const { isDesktop } = usePageWidthBreakpoint();
	// Once a user is on the new side navigation, the legacy left column (EmployeeCard
	// + SideNavigation) is replaced by the global SideNavShell. Suppress it here so
	// every InfoPageTemplate consumer gets the wider content area without per-page edits.
	const suppressLegacySideMenu = useIsNewNavigation();

	const getSideNavigationMenu = () => {
		if (sideNavigationMenuOverride) {
			return sideNavigationMenuOverride;
		} else if (navigationLinks) {
			return <SideNavigation activeKey={activeNavigationLinkKey} items={navigationLinks} />;
		}
		return null;
	};

	const showSideMenu = !suppressLegacySideMenu && (employeeID || navigationLinks || sideNavigationMenuOverride);
	const useSingleColumnLayout = suppressLegacySideMenu && !showSideMenu;

	return (
		<div className={css.infoPageTemplate}>
			{pageTitle && <PageTitle title={pageTitle} />}
			<div className={`${css.contentWindow}`}>
				<Spacer height={3} />
				<Grid
					cols={12}
					gap={isDesktop ? 3 : 0}
					padding={7}
					className={`${css.gridContainer} ${useSingleColumnLayout ? css.singleColumnGridContainer : ""}`}
				>
					{showSideMenu ? (
						<GridCol className={isDesktop ? css.sidemenuCol : ""} key={0} span={isDesktop ? 12 : 2}>
							<div className={css.sideMenuContainer}>
								{employeeID && (
									<div>
										<EmployeeCard maxWidth={isDesktop ? "208px" : null} employeeID={employeeID} />
										<Spacer height={2} />
									</div>
								)}
								{getSideNavigationMenu()}
							</div>
						</GridCol>
					) : null}
					<GridCol key={1} span={isDesktop ? 12 : 10} className={isDesktop ? css.contentCol : ""}>
						<div data-testid="mainContent" className={css.MainContent}>
							{pageHeading && <div className={css.pageHeading}>{pageHeading}</div>}
							<Grid className={`${css.controlsContainer} ${!isDesktop ? css.mobile : ""}`}>
								<GridCol
									key={2}
									span={isDesktop ? 6 : 12}
									className={`${css.controlsLeft} ${!isDesktop ? css.mobile : ""}`}
								>
									{toolbarActionRow}
								</GridCol>
								{actionButtons && (
									<GridCol
										key={3}
										span={isDesktop ? 6 : 12}
										className={`${css.controlsRight} ${!isDesktop ? css.mobile : ""}`}
									>
										{actionButtons?.map((actionButton, index) => (
											<div key={`pageTemplateActionButton-${index}`} className={css.actionButton}>
												{actionButton}
											</div>
										))}
									</GridCol>
								)}
							</Grid>
							<Spacer height={1} />
							<div
								className={`${css.controlsContainer} ${css.lowerControls} ${
									!isDesktop ? css.mobile : ""
								}`}
							>
								<div key={2} className={`${css.controlsLeft} ${!isDesktop ? css.mobile : ""}`}>
									{toolbarInformationRow ? <div>{toolbarInformationRow}</div> : <Spacer height={1} />}
								</div>
								{secondaryActionButtons && (
									<div key={3} className={`${css.controlsRight} ${!isDesktop ? css.mobile : ""}`}>
										{secondaryActionButtons?.map((actionButton, index) => (
											<div key={`pageTemplateActionButton-${index}`} className={css.actionButton}>
												{actionButton}
											</div>
										))}
									</div>
								)}
							</div>
							{toolbarInformationRow || secondaryActionButtons ? <Spacer height={1} /> : null}

							<div>{mainContent}</div>
							<Spacer height={3} />
						</div>
					</GridCol>
				</Grid>
			</div>
			{drawer}
		</div>
	);
};

export default InfoPageTemplate;
